import React from 'react';
import { Target, Zap, Clock, CheckCircle } from 'lucide-react';

/**
 * Session Summary Component
 * Displays session statistics and estimated rewards
 */
export default function SessionSummary({ totalDuration, targetDuration, estimatedXP, workflow }) {
  
  const completionRequirements = [
    { label: 'Complete all segments', completed: workflow?.length > 0 },
    { label: 'Maintain focus throughout', completed: false },
    { label: 'Hit timing targets', completed: totalDuration >= targetDuration * 0.8 }
  ];

  const toolCounts = workflow?.reduce((acc, segment) => {
    acc[segment?.tool] = (acc?.[segment?.tool] || 0) + 1;
    return acc;
  }, {});

  const toolNames = {
    'breath-coach': 'Breath Coach',
    'pitch-coach': 'Pitch Coach',
    'audition-songs': 'Audition Songs'
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-4">Session Summary</h2>
      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span className="text-white/60 text-xs">Total Duration</span>
          </div>
          <div className="text-2xl font-bold text-white">{totalDuration} min</div>
        </div>

        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-white/60 text-xs">Estimated XP</span>
          </div>
          <div className="text-2xl font-bold text-white">{estimatedXP}</div>
        </div>
      </div>
      {/* Tool Breakdown */}
      <div className="mb-6">
        <h3 className="text-white/80 text-sm font-medium mb-3">Tools Used</h3>
        <div className="space-y-2">
          {Object.entries(toolCounts)?.map(([toolId, count]) => (
            <div key={toolId} className="flex items-center justify-between text-sm">
              <span className="text-white/60">{toolNames?.[toolId]}</span>
              <span className="text-white font-medium">{count}x</span>
            </div>
          ))}
        </div>
      </div>
      {/* Completion Requirements */}
      <div>
        <h3 className="text-white/80 text-sm font-medium mb-3">Completion Requirements</h3>
        <div className="space-y-2">
          {completionRequirements?.map((req, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <CheckCircle 
                className={`w-4 h-4 ${req?.completed ? 'text-green-400' : 'text-white/30'}`} 
              />
              <span className={req?.completed ? 'text-white' : 'text-white/60'}>
                {req?.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Achievement Preview */}
      <div className="mt-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-400/30">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-purple-300" />
          <span className="text-purple-200 font-medium text-sm">Achievement Potential</span>
        </div>
        <p className="text-white/80 text-xs">
          Complete this session to earn <span className="font-bold text-white">{estimatedXP} XP</span> and 
          maintain your practice streak! 🔥
        </p>
      </div>
    </div>
  );
}